import {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { captureException } from 'utils/exceptionUtils';
import { useAuth } from 'context/auth';
import CallIntegrationType from 'crono-fe-common/types/enums/callIntegrationType';
import AircallWorkspace, {
  AircallSendError,
  CallEndedData,
  OutgoingCallData,
} from 'aircall-everywhere';
import { AircallContextType } from './types';
import { AIRCALL_UI_CONTAINER_ID } from 'components/Aircall/AircallFloatingUI';
import { AircallContext } from './contexts';

export const AircallProvider: FC<PropsWithChildren> = ({ children }) => {
  const sdk = useRef<AircallWorkspace>();
  const { user } = useAuth();
  const isAircallEnabled =
    user?.callIntegrationType === CallIntegrationType.AIRCALL;
  const [isFloatingUIVisible, setIsFloatingUIVisible] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const onRingingRef = useRef<
    ((event: OutgoingCallData) => void) | undefined
  >();
  const onHangupRef = useRef<((data: CallEndedData) => void) | undefined>();

  const init = () => {
    try {
      if (!isAircallEnabled) {
        return;
      }

      if (sdk.current) {
        console.warn('Aircall SDK is already initialized');
        return;
      }

      sdk.current = new AircallWorkspace({
        domToLoadWorkspace: `#${AIRCALL_UI_CONTAINER_ID}`,
        size: 'auto',
      });

      sdk.current.on('incoming_call', () => {
        setIsFloatingUIVisible(true);
      });

      sdk.current.on('outgoing_call', (e) => {
        setIsCallActive(true);
        onRingingRef.current?.(e);
      });

      sdk.current.on('call_ended', (e) => {
        setIsCallActive(false);
        // call_ended fires for any workspace call (incoming included), so
        // consume the callbacks once a call ends to avoid replaying them on
        // unrelated calls
        const onHangup = onHangupRef.current;
        onRingingRef.current = undefined;
        onHangupRef.current = undefined;
        onHangup?.(e);
      });
    } catch (error) {
      console.error('Failed to initialize Aircall SDK:', error);
      captureException(error);
    }
  };

  useEffect(() => {
    init();
  }, [isAircallEnabled]);

  const makeCall: AircallContextType['makeCall'] = (number, options = {}) => {
    const callSdk = sdk.current;

    if (!callSdk) {
      const errorMsg = `Could not find Aircall SDK when attempting to execute a call to ${number}`;
      console.error(errorMsg);
      captureException(new Error(errorMsg));
      return;
    }

    const executeCall = () => {
      onRingingRef.current = options.onRinging;
      onHangupRef.current = options.onHangup;

      callSdk.send(
        'dial_number',
        { phone_number: number },
        (success, dataOrError) => {
          setIsFloatingUIVisible(success);

          if (success) {
            options.onDial?.(success, dataOrError);
          } else {
            options.onDial?.(success, dataOrError as AircallSendError);
          }

          if (!success) {
            onRingingRef.current = undefined;
            onHangupRef.current = undefined;
            const error = dataOrError as AircallSendError;
            console.error(
              'Failed to dial number using Aircall SDK',
              error.code,
              error.message,
            );
            captureException(
              new Error(
                `Failed to dial ${number} using Aircall SDK. Error code: ${error.code}. Error message: ${error.message}`,
              ),
            );
          }
        },
      );
    };

    callSdk.isLoggedIn((isLoggedIn) => {
      if (!isLoggedIn) {
        setIsFloatingUIVisible(true);
        return;
      }

      executeCall();
    });
  };

  const openFloatingUI = () => {
    setIsFloatingUIVisible(true);
  };

  const closeFloatingUI = () => {
    setIsFloatingUIVisible(false);
  };

  const value = {
    makeCall,
    openFloatingUI,
    closeFloatingUI,
    isFloatingUIVisible,
    isCallActive,
  };

  return (
    <AircallContext.Provider value={value}>{children}</AircallContext.Provider>
  );
};

export const useAircall = () => {
  const context = useContext(AircallContext);
  if (context === undefined) {
    throw new Error('useAircall must be used within a AircallProvider');
  }
  return context;
};
